/**
 * Admin Service - Handles admin-only operations
 * SECURITY: Frontend check is UX only - real validation happens in Cloud Functions
 */

import { getFunctions, httpsCallable } from 'firebase/functions';
import { getFirestore, collection, query, where, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { authService } from './auth.js';

// SECURITY NOTE: This is for UI purposes only.
// Backend validates admin access via Cloud Functions with server-side ADMIN_EMAIL
const ADMIN_EMAIL = import.meta.env?.VITE_ADMIN_EMAIL;

// silent

class AdminService {
  constructor() {
    this.metricsCache = null;
    this.cacheTimestamp = 0;
    this.cacheTTL = 5 * 60 * 1000; // 5 minutes
    this.ticketsUnsubscribe = null;
  }

  /**
   * Check if current user is the system admin (sync)
   * NOTE: This is for UI purposes only - backend validates separately
   * WARNING: May return false during auth initialization - use isAdminAsync for reliable check
   */
  isAdmin() {
    if (!ADMIN_EMAIL) return false; // Fail closed if not configured
    const currentUser = authService.currentUser;
    return currentUser?.email === ADMIN_EMAIL;
  }

  /**
   * Check if current user is admin (async - waits for auth)
   * Use this when you need reliable admin check during page load
   */
  async isAdminAsync() {
    if (!ADMIN_EMAIL) return false;
    await authService.waitForAuth();
    const currentUser = authService.currentUser;
    return currentUser?.email === ADMIN_EMAIL;
  }

  /**
   * Check if current user is system admin via Firebase custom claims OR email
   * First checks custom claims, then falls back to email check
   * This is used for Firestore rules validation on frontend
   * @returns {Promise<boolean>}
   */
  async isSystemAdmin() {
    try {
      await authService.waitForAuth();
      const auth = authService.auth;
      if (!auth?.currentUser) return false;

      // First check custom claims
      const idTokenResult = await auth.currentUser.getIdTokenResult();
      if (idTokenResult.claims.admin === true) {
        return true;
      }

      // Fallback: Check by email (same as isAdmin)
      if (ADMIN_EMAIL && auth.currentUser.email === ADMIN_EMAIL) {
        return true;
      }

      return false;
    } catch (error) {
      return false;
    }
  }

  /**
   * Verify admin access - throws error if not admin
   */
  verifyAdmin() {
    if (!this.isAdmin()) {
      throw new Error('Admin access required');
    }
  }

  /**
   * Get system metrics with caching
   */
  async getSystemMetrics(forceRefresh = false) {
    this.verifyAdmin();

    // Return cached data if valid
    if (!forceRefresh && this.metricsCache &&
        (Date.now() - this.cacheTimestamp) < this.cacheTTL) {
      return this.metricsCache;
    }

    try {
      const functions = getFunctions();
      const getMetrics = httpsCallable(functions, 'getAdminSystemMetrics');
      const result = await getMetrics();

      this.metricsCache = result.data;
      this.cacheTimestamp = Date.now();

      return result.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get support tickets
   */
  async getTickets(filters = {}) {
    this.verifyAdmin();

    try {
      const functions = getFunctions();
      const getTickets = httpsCallable(functions, 'adminGetTickets');
      const result = await getTickets(filters);
      return result.data.tickets;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Subscribe to real-time ticket updates
   */
  subscribeToTickets(filters, callback) {
    this.verifyAdmin();

    // Unsubscribe from previous listener
    if (this.ticketsUnsubscribe) {
      this.ticketsUnsubscribe();
      this.ticketsUnsubscribe = null;
    }

    try {
      const db = getFirestore();
      const ticketsRef = collection(db, 'support_tickets');

      let q = query(ticketsRef, orderBy('createdAt', 'desc'), limit(100));

      if (filters?.status && filters.status !== 'all') {
        q = query(
          ticketsRef,
          where('status', '==', filters.status),
          orderBy('createdAt', 'desc'),
          limit(100)
        );
      }

      this.ticketsUnsubscribe = onSnapshot(q, (snapshot) => {
        const tickets = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate?.()?.toISOString() || null,
          updatedAt: doc.data().updatedAt?.toDate?.()?.toISOString() || null
        }));
        callback(tickets);
      }, () => {
        // silent
      });

      return this.ticketsUnsubscribe;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Update ticket status
   */
  async updateTicket(ticketId, status, adminResponse = null) {
    this.verifyAdmin();

    try {
      const functions = getFunctions();
      const updateTicket = httpsCallable(functions, 'adminUpdateTicket');
      const result = await updateTicket({ ticketId, status, adminResponse });
      return result.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get detailed agency information
   */
  async getAgencyDetails(agencyId) {
    this.verifyAdmin();

    try {
      const functions = getFunctions();
      const getDetails = httpsCallable(functions, 'adminGetAgencyDetails');
      const result = await getDetails({ agencyId });
      return result.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Clean agency database (remove orphaned data)
   */
  async cleanAgencyDatabase(agencyId) {
    this.verifyAdmin();

    try {
      const functions = getFunctions();
      const cleanDatabase = httpsCallable(functions, 'adminCleanAgencyDatabase');
      const result = await cleanDatabase({ agencyId });
      return result.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Migrate agency encryption
   */
  async migrateAgencyEncryption(agencyId) {
    this.verifyAdmin();

    try {
      const functions = getFunctions();
      const migrateEncryption = httpsCallable(functions, 'adminMigrateAgencyEncryption');
      const result = await migrateEncryption({ agencyId });
      return result.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get agency encryption status
   */
  async getAgencyEncryptionStatus(agencyId) {
    this.verifyAdmin();

    try {
      const functions = getFunctions();
      const checkStatus = httpsCallable(functions, 'checkEncryptionStatus');
      const result = await checkStatus({ agencyId });
      return result.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Delete all agency data completely
   */
  async deleteAgencyData(agencyId) {
    this.verifyAdmin();

    try {
      const functions = getFunctions();
      const deleteData = httpsCallable(functions, 'adminDeleteAgencyData');
      const result = await deleteData({ agencyId, confirmDelete: 'DELETE' });
      return result.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get subscription details for an agency
   */
  async getSubscriptionDetails(agencyId) {
    this.verifyAdmin();

    try {
      const functions = getFunctions();
      const getDetails = httpsCallable(functions, 'adminGetSubscriptionDetails');
      const result = await getDetails({ agencyId });
      return result.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Update subscription for an agency
   */
  async updateSubscription(agencyId, updates, reason) {
    this.verifyAdmin();

    try {
      const functions = getFunctions();
      const updateSub = httpsCallable(functions, 'adminUpdateSubscription');
      const result = await updateSub({ agencyId, updates, reason });
      return result.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Toggle free access for an agency
   */
  async toggleFreeAccess(agencyId, freeAccess, reason) {
    this.verifyAdmin();

    try {
      const functions = getFunctions();
      const toggleAccess = httpsCallable(functions, 'adminToggleFreeAccess');
      const result = await toggleAccess({ agencyId, freeAccess, reason });
      return result.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get payment history for an agency
   */
  async getPaymentHistory(agencyId, limit = 20) {
    this.verifyAdmin();

    try {
      const functions = getFunctions();
      const getHistory = httpsCallable(functions, 'adminGetPaymentHistory');
      const result = await getHistory({ agencyId, limit });
      return result.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get all refund requests with user info
   */
  async getRefundRequests() {
    this.verifyAdmin();

    try {
      const functions = getFunctions();
      const getRefunds = httpsCallable(functions, 'adminGetRefundRequests');
      const result = await getRefunds();
      return result.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Process refund via PayPal API
   */
  async processRefund(refundRequestId) {
    this.verifyAdmin();

    try {
      const functions = getFunctions();
      const processRefund = httpsCallable(functions, 'adminProcessRefund');
      const result = await processRefund({ refundRequestId });
      return result.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Reject refund request
   */
  async rejectRefund(refundRequestId, reason) {
    this.verifyAdmin();

    try {
      const functions = getFunctions();
      const rejectRefund = httpsCallable(functions, 'adminRejectRefund');
      const result = await rejectRefund({ refundRequestId, reason });
      return result.data;
    } catch (error) {
      throw error;
    }
  }

  // ==================== TASK SYSTEM ====================

  async createTask(ticketId) {
    this.verifyAdmin();
    const functions = getFunctions();
    const fn = httpsCallable(functions, 'adminCreateTask');
    const result = await fn({ ticketId });
    return result.data;
  }

  async getTasks(filters = {}) {
    this.verifyAdmin();
    const functions = getFunctions();
    const fn = httpsCallable(functions, 'adminGetTasks');
    const result = await fn(filters);
    return result.data.tasks;
  }

  async updateTaskStatus(taskId, status) {
    this.verifyAdmin();
    const functions = getFunctions();
    const fn = httpsCallable(functions, 'adminUpdateTaskStatus');
    const result = await fn({ taskId, status });
    return result.data;
  }

  /**
   * Clear cache
   */
  clearCache() {
    this.metricsCache = null;
    this.cacheTimestamp = 0;
  }

  /**
   * Cleanup - unsubscribe from listeners
   */
  cleanup() {
    if (this.ticketsUnsubscribe) {
      this.ticketsUnsubscribe();
      this.ticketsUnsubscribe = null;
    }
    this.clearCache();
  }

}

export const adminService = new AdminService();
